import { AxiosResponse } from "axios";
import { getCulture } from "CMS/utils";
import { EmailInput, Modal, UsernameInput } from "components";
import CulturesInterfaceDropdown from "components/dropdowns/reactselect/CulturesInterfaceDropdown";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import { BookkeepingMultiModalPicker, ValidatedSelectedBookkeepings } from "components/Picker/Bookkeeping/BookkeepingMultiModalPicker";
import FreeFieldProfileModalPicker from "components/Picker/FreefieldProfile/FreefieldProfileModalPicker";
import React from "react";
import { useCustomQuery } from "utilities/hookutils";
import { UserFirstLastName } from "./UserFirstLastName";
import { UpgradeConfirmation } from "./UpgradeConfirmation";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  environment: server.dto.Environments;
  submitFunc: (formState: NewUserFormState) => Promise<AxiosResponse<server.dto.ApiResponseWrapper<number>, any>>;
  redirectUrl: (userId: number) => string;
}

interface NewUserFormState {
  firstName: string;
  lastName: string;
  email: string;
  language: server.dto.Culture;
  username: string;
  freeFieldProfile: server.dto.FreeFieldProfileSmall;
  bookkeepings: server.dto.BookkeepingSmall[];
}

export const NewUserModal = (props: Props) => {
  const [formState, setFormState] = React.useState<NewUserFormState>({
    firstName: "",
    lastName: "",
    email: "",
    language: getCulture(site.currentUser.culture),
    username: "",
    freeFieldProfile: null,
    bookkeepings: [],
  });

  const maximumAccountsReachedApi = useCustomQuery({
    queryFn: async () => window.CoreApi.Subscription.CheckMaximumAccountsReached(),
    queryKey: ["maximumAccountsReached"],
    enabled: props.environment === "internal",
  });

  const maximumAccountsReached = maximumAccountsReachedApi.data?.data?.data?.maximumReached || false;
  const upgradeMessage = maximumAccountsReachedApi.data?.data?.data?.upgradeMessage || null;

  const onChange = (newData: Partial<NewUserFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const submit = () => props.submitFunc(formState);

  const onSuccess = (userId: number) => {
    window.location.href = props.redirectUrl(userId);
  }

  return <Modal
    isOpen={props.isOpen}
    title={props.title}
    handleClose={props.onClose}
    width="small"
  >
    <div className="grow1 scrollbox-auto">
      <EditSectionTemplate
        submit={submit}
        noPanels
        onSuccess={onSuccess}
        sections={[{
          key: "userDetails",
          type: "regular",
          title: props.title,
          fields: [{
            key: "name",
            label: RESX.GeneralLabels.Name,
            renderEditNew: () => <UserFirstLastName
              firstName={formState.firstName}
              lastName={formState.lastName}
              onChange={(firstName: string, lastName: string) => {
                onChange({ firstName, lastName });
              }}
            />
          }, {
            key: "email",
            label: RESX.GeneralLabels.Email,
            renderEditNew: () => <EmailInput
              value={formState.email}
              onChange={(email: string) => { onChange({ email }) }}
              validatorKey="email"
              emailCanBeBlank={false}
            />
          }, {
            key: "language",
            label: RESX.Language.resxLanguage,
            renderEditNew: () => <CulturesInterfaceDropdown
              value={formState.language}
              onChange={(language: server.dto.Culture) => { onChange({ language }) }}
            />
          }, {
            key: "username",
            label: RESX.SoUser.Username,
            renderEditNew: () => <UsernameInput
              value={formState.username}
              onChange={(username) => { onChange({ username }) }}
              validatorKey="username"
            />
          }, {
            key: "password",
            label: RESX.PersonalSettings.Password,
            renderEditNew: () => <div className="main-section-template-fields-padding">{RESX.SoUser.WillBeAutomaticallyGenerated}</div>
          }, {
            key: "freeFieldPermissionProfileLabel",
            label: RESX.SoUser.Permissions,
            hideLabel: true,
            renderEditNew: () => <div className="main-section-template-fields-padding">
              <h3>{RESX.SoUser.Permissions}</h3>
            </div>
          }, {
            key: "freeFieldPermissionProfile",
            label: RESX.FreeField.PermissionProfile,
            renderEditNew: () => <div className="main-section-template-fields-padding">
              <FreeFieldProfileModalPicker
                environment={props.environment}
                canCreateNew={false}
                nonInPageEditing={{
                  onChange: v => onChange({ freeFieldProfile: v }),
                  value: formState.freeFieldProfile,
                  validatorKey: "freeFieldProfile",
                  required: true,
                }}
              />
            </div>,
          }, {
            key: "bookkeepings",
            label: RESX.SoUser.AccessToBookKeepings,
            hidden: props.environment !== "internal",
            isEmpty: formState.bookkeepings.length === 0,
            renderEditNew: () => <ValidatedSelectedBookkeepings
              value={formState.bookkeepings}
              validatorKey="bookkeepings"
            />,
            inPageEdit: {
              render: ({ cancel, submit }) => <BookkeepingMultiModalPicker
                inPageEditing={{ cancel }}
                value={formState.bookkeepings}
                onChange={bookkeepings => {
                  onChange({ bookkeepings });
                  submit();
                }}
                canCreateNew={false}
                required={true}
                bookkeepingSecurityScope="allForClient"
                validatorKey="bookkeepings"
              />,
              fullWidth: true,
            },
          }, {
            key: "upgradeSubscriptionLabel",
            label: RESX.Subscription.resxSubscription,
            hidden: !maximumAccountsReached,
            hideLabel: true,
            renderEditNew: () => <div className="main-section-template-fields-padding">
              <h3>{RESX.Subscription.resxSubscription}</h3>
            </div>
          }, {
            key: "upgradeSubscriptionCheck",
            label: RESX.Subscription.Upgrade,
            hidden: !maximumAccountsReached,
            renderEditNew: () => <UpgradeConfirmation
              upgradeMessage={upgradeMessage}
            />,
          }]
        }]}
      />
    </div>
  </Modal>
}